import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'contexts/user';
import { AppDataContext } from 'contexts/appData';
import { PeriodsEnum, StatsEnum } from './helpers';
import { useGetProductAnalytics } from 'queries/products/useGetProductAnalytics';
import { useGetProductAnalyticsTotal } from '../../../../queries/products/useGetProductAnalyticsTotal';
import { useGetProductRecommendations } from '../../../../queries/products/useGetProductRecommendations';
import { useDebounce } from '../../../../libs/hooks/useDebounce';
import { makeCioProductItem } from '../../../../libs/helpers';
import * as customerio from '../../../../libs/customerio';

const AnalyticsContext = createContext(null);

const StatsWindowMap = {
  [PeriodsEnum.OneWeek]: '7d',
  [PeriodsEnum.TwoWeeks]: '14d',
  [PeriodsEnum.OneMonth]: '30d',
  [PeriodsEnum.SixMonths]: '180d',
  [PeriodsEnum.Year]: '365d',
};

const toPercent = (part, whole) => (Number(whole) ? Math.round((Number(part) / Number(whole)) * 100) : 0);

const getChangePercent = (current, difference) => {
  const previous = current - difference;
  if (!previous) return current ? 100 : 0;

  return Math.round((difference / previous) * 100);
};

const getImpressionsChange3dPercent = (dailyList) => {
  if (dailyList.length < 7) return 0;

  const sumImpressions = (days) => days.reduce((sum, day) => sum + Number(day.displays || 0), 0);
  const lastThreeDays = sumImpressions(dailyList.slice(-3));
  const previousThreeDays = sumImpressions(dailyList.slice(-6, -3));

  return getChangePercent(lastThreeDays, lastThreeDays - previousThreeDays);
};

const getAnalyticsUrl = (appUrl, lang, productId) =>
  `${appUrl}${lang}/myproducts?analyticsProductId=${productId}`;

const getPromoUrl = (appUrl, lang, productId) =>
  `${appUrl}${lang}/myproducts?promoProductId=${productId}`;

const increaseAnalyticsOpenCount = (productId) => {
  const key = `listing_analytics_open_count_${productId}`;
  try {
    const count = (Number(localStorage.getItem(key)) || 0) + 1;
    localStorage.setItem(key, String(count));
    return count;
  } catch (e) {
    return 1;
  }
};

export const useAnalyticsContext = () => {
  return useContext(AnalyticsContext);
};

export const AnalyticsProvider = ({ children, productId, setProductId }) => {
  const [activePeriod, setActivePeriod] = useState(PeriodsEnum.OneWeek);
  const [activeTab, setActiveTab] = useState(StatsEnum.Displays);

  const debouncedProductId = useDebounce(productId, 300)

  const { currentList, currentTotal, difference,  isLoading, isPending, isFetching } = useGetProductAnalytics({ productId: debouncedProductId, periodId: activePeriod });
  const { total, sellStatistics, ranking, product, nextProductId, prevProductId } = useGetProductAnalyticsTotal({ productId: debouncedProductId });
  const { recommendations } = useGetProductRecommendations({ productId: debouncedProductId });

  const { user } = useContext(UserContext);
  const { CONFIG, getLang } = useContext(AppDataContext);

  const isTrackedRef = useRef(false);

  useEffect(() => {
    if (!user?.ID || !product || isFetching || String(product.product_id) !== String(debouncedProductId)) return;
    if (isTrackedRef.current) return;
    isTrackedRef.current = true;

    const impressions = Number(currentTotal?.displays || 0);
    const views = Number(currentTotal?.views || 0);
    const calls = Number(currentTotal?.phone_views || 0);
    const impressionsDifference = Number(difference?.displays || 0);

    customerio.listingAnalyticsViewed({
      currency: 'GEL',
      value: Number(product.price),
      ...makeCioProductItem(product),
      acquisition_source: 'my_listings',
      analytics_url: getAnalyticsUrl(CONFIG.HOST.appUrl, getLang, debouncedProductId),
      promo_url: getPromoUrl(CONFIG.HOST.appUrl, getLang, debouncedProductId),

      stats_window: StatsWindowMap[activePeriod],
      impressions,
      impressions_change: impressionsDifference,
      impressions_change_3d_pct: getImpressionsChange3dPercent(currentList),
      impressions_change_7d_pct: activePeriod === PeriodsEnum.OneWeek ? getChangePercent(impressions, impressionsDifference) : null,
      views,
      views_change: Number(difference?.views || 0),
      calls,
      calls_change: Number(difference?.phone_views || 0),
      favorites: Number(currentTotal?.favorites || 0),
      favorites_change: Number(difference?.favorites || 0),
      ranking_position: Number(ranking || 0),
      ctr: toPercent(views, impressions),
      view_to_call_rate: toPercent(calls, views),

      sold_count: Number(sellStatistics?.sold_products_count || 0),
      returned_count: Number(sellStatistics?.returned_products_count || 0),
      cancelled_count: Number(sellStatistics?.cancelled_products_count || 0),
      stock_count: Number(sellStatistics?.current_stock || 0),

      analytics_open_count: increaseAnalyticsOpenCount(debouncedProductId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, product, isFetching, debouncedProductId]);

  return (
    <AnalyticsContext.Provider
      value={{
        chartData: currentList,
        currentTotal,
        total,
        sellStatistics,
        difference,
        isLoading,
        isPending,
        isFetching,
        activePeriod,
        setActivePeriod,
        activeTab,
        setActiveTab,
        recommendations,
        ranking,
        productId,
        debouncedProductId,
        setProductId,
        product,
        nextProductId,
        prevProductId
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
